import React, { useState, useEffect } from 'react';

import { AutoComplete } from 'antd';
import styled from 'styled-components';

import {
  usePlacesAutocomplete,
  PlaceSuggestion as Suggestion,
} from '../../hooks/usePlacesAutocomplete';
import { PlacesAutocompleteAddon } from './PlacesAutocompleteAddon';
import { getIsoCountry } from './utils';

interface PlacesAutocompleteViewProps {
  id: string;
  placeholder?: string | undefined;
  handleBlur?: () => void;
  handleChange: (data: string) => void;
  handleCountryChange: (value: string) => void;
  handleSearch: () => void;
  handleSelect: (address: string) => void;
  places: Suggestion[];
  defaultValue?: string;
  value?: string;
  selectedCountry: string;
}
interface PlacesAutocompleteProps {
  id: string;
  onSelectChange: (place?: Suggestion) => void;
  placeholder?: string;
  defaultValue?: string;
  defaultCountry?: string;
}

const { Option } = AutoComplete;

export const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  id,
  onSelectChange,
  placeholder,
  defaultValue,
  defaultCountry,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string>(
    defaultCountry || 'France'
  );
  const {
    value,
    setValue,
    suggestions: { status, data },
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: getIsoCountry(selectedCountry) },
      types: ['address'],
    },
    debounce: 200,
  });

  useEffect(() => {
    !!defaultValue && setValue(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [places, setPlaces] = useState<Suggestion[]>([]);

  useEffect(() => {
    if (status === 'OK') {
      setPlaces(data);
    } else {
      setPlaces([]);
    }
  }, [status, data]);

  const handleBlur = () => {
    !value && setPlaces([]);
  };

  const handleChange = (data: string) => {
    setValue(data);
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    emptyAddressField();
  };

  const handleSelect = (address: string) => {
    const selectedPlace = places.find(
      (place: Suggestion) => place.description === address
    );
    onSelectChange(selectedPlace);
  };

  const handleSearch = () => {
    // This is called by antd AutoComplete but we handle updates via useEffect
  };

  const emptyAddressField = () => {
    if (value) {
      setValue('');
      setPlaces([]);
    }
  };

  const props = {
    id,
    defaultValue,
    value,
    placeholder,
    handleBlur,
    handleChange,
    handleCountryChange,
    handleSearch,
    handleSelect,
    selectedCountry,
  };

  return <PlacesAutocompleteView {...props} places={places} />;
};

export const PlacesAutocompleteView: React.FC<PlacesAutocompleteViewProps> = ({
  defaultValue,
  handleChange,
  handleCountryChange,
  handleBlur,
  handleSearch,
  handleSelect,
  id,
  placeholder,
  places,
  selectedCountry,
  value,
}) => {
  const children =
    places.length > 0 &&
    places.map((place: Suggestion) => {
      return (
        <Option key={place.place_id} value={place.description}>
          {place.description}
        </Option>
      );
    });

  return (
    <AutocompleteContainer>
      <PlacesAutocompleteAddon
        defaultValue={selectedCountry}
        onChange={handleCountryChange}
      />
      <AutoComplete
        id={id}
        defaultValue={defaultValue}
        style={{ width: '100%' }}
        size="large"
        onSearch={handleSearch}
        onChange={handleChange}
        onSelect={handleSelect}
        placeholder={placeholder || 'Adresse'}
        data-testid="autoCompletePlaces"
        onBlur={handleBlur}
        value={value}
      >
        {children}
      </AutoComplete>
    </AutocompleteContainer>
  );
};

const AutocompleteContainer = styled.div`
  display: flex;

  .ant-select {
    &:first-child {
      flex: 0;

      .ant-select-selector {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    + .ant-select {
      .ant-select-selector {
        border-left: 0;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`;
